import React, { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Eye, EyeOff, Globe2, Search, ArrowLeftRight, RefreshCw } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * AdminCurrencies — B2C Currency Activation
 *
 * NOT a CRUD page. The FX rate book lives entirely in `/admin/fx` (fx_client +
 * live rate feeds). This page controls **which currencies are exposed to B2C
 * customers** in the storefront currency picker. Conversion math is untouched.
 */
export default function AdminCurrencies() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [savingCode, setSavingCode] = useState(null);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/fx/b2c-visibility");
      setRows(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load currencies");
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (r) => {
    if (r.is_base) { toast.info("USD is the base currency — always visible on B2C"); return; }
    setSavingCode(r.code);
    const next = !r.visible;
    try {
      await api.patch(`/admin/fx/b2c-visibility/${r.code}`, { visible: next });
      setRows((prev) => prev.map((x) => x.code === r.code ? { ...x, visible: next } : x));
      toast.success(`${r.code} ${next ? "shown" : "hidden"} on B2C`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSavingCode(null); }
  };

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) =>
      r.code.toLowerCase().includes(needle) ||
      (r.name || "").toLowerCase().includes(needle)
    );
  }, [rows, q]);

  const counts = useMemo(() => ({
    visible: rows.filter((r) => r.visible).length,
    hidden:  rows.filter((r) => !r.visible).length,
    total:   rows.length,
  }), [rows]);

  const fmtDate = (s) => {
    if (!s) return "—";
    const d = new Date(s);
    return isNaN(d) ? "—" : d.toLocaleDateString("en-GB") + " " + d.toLocaleTimeString("en-GB").slice(0, 5);
  };

  return (
    <div className="space-y-4" data-testid="admin-currencies">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-semibold text-[var(--brand-accent)] flex items-center gap-2">
            <Globe2 className="w-6 h-6 text-[var(--brand-primary)]" /> B2C Currency Activation
          </h1>
          <p className="text-slate-500 mt-1 max-w-3xl text-sm leading-relaxed">
            Choose which currencies the storefront currency picker shows to B2C customers. The conversion math comes from <Link to="/admin/fx" className="text-[var(--brand-primary-dark)] font-semibold underline-offset-2 hover:underline">FX Rates</Link> — toggling a row here only affects <strong>visibility</strong>, never rates or margins. <strong className="text-[var(--brand-accent)]">USD</strong> is the base currency and always visible.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} disabled={busy} data-testid="currencies-refresh-btn"
                  className="rounded-full px-3 py-2 text-xs font-semibold bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 flex items-center gap-1.5">
            <RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="currencies-stats">
        <Card className="p-4">
          <div className="text-xs uppercase font-bold tracking-wide text-emerald-700">Visible on B2C</div>
          <div className="font-heading text-3xl font-bold text-emerald-700 mt-1" data-testid="stat-visible">{counts.visible}</div>
        </Card>
        <Card className="p-4">
          <div className="text-xs uppercase font-bold tracking-wide text-rose-700">Hidden</div>
          <div className="font-heading text-3xl font-bold text-rose-700 mt-1" data-testid="stat-hidden">{counts.hidden}</div>
        </Card>
        <Card className="p-4">
          <div className="text-xs uppercase font-bold tracking-wide text-slate-500">In rate book</div>
          <div className="font-heading text-3xl font-bold text-[var(--brand-accent)] mt-1" data-testid="stat-total">{counts.total}</div>
          <div className="text-[10px] text-slate-400 mt-0.5">From FX Rates</div>
        </Card>
      </div>

      <div className="relative max-w-md">
        <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <Input data-testid="currencies-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by code or name" className="pl-9" />
      </div>

      <Card className="overflow-x-auto">
        <Table className="min-w-[800px]">
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Name</TableHead>
              <TableHead className="text-right">Effective Rate (incl. margin)</TableHead>
              <TableHead>B2C Preview</TableHead>
              <TableHead>Last Toggled</TableHead>
              <TableHead className="text-center">Show on B2C</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((r) => (
              <TableRow key={r.code} data-testid={`currency-row-${r.code}`}
                        className={r.visible ? "" : "bg-rose-50/40"}>
                <TableCell className="font-mono font-bold text-[var(--brand-accent)]">
                  {r.code}
                  {r.is_base && <span className="ml-2 text-[10px] uppercase font-semibold tracking-wide text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">base</span>}
                </TableCell>
                <TableCell>{r.name}</TableCell>
                <TableCell className="text-right font-mono text-xs">
                  {/* Show the CUSTOMER-FACING "USD → local unit" rate — that's
                      what an admin actually wants to sanity-check ("how much
                      of this currency does the customer receive per USD?").
                      Falls back to the mid-market inverse when the backend
                      doesn't expose `usd_to_unit` yet (legacy responses). */}
                  {(() => {
                    const utu = r.usd_to_unit != null ? Number(r.usd_to_unit)
                              : (r.rate_to_usd ? (1 / Number(r.rate_to_usd)) * (1 + Number(r.margin_pct || 0) / 100) : null);
                    return utu != null ? utu.toFixed(4) : "—";
                  })()}
                  <span className="text-slate-400"> {r.code}/USD</span>
                  {r.margin_pct ? <div className="text-[10px] text-slate-400">+{r.margin_pct}% margin</div> : null}
                </TableCell>
                <TableCell className="text-xs text-slate-600 whitespace-nowrap">
                  {(() => {
                    const utu = r.usd_to_unit != null ? Number(r.usd_to_unit)
                              : (r.rate_to_usd ? (1 / Number(r.rate_to_usd)) * (1 + Number(r.margin_pct || 0) / 100) : 1);
                    return (
                      <>
                        <span className="font-mono">$100</span> <ArrowLeftRight className="w-3 h-3 inline mx-1 text-slate-400" /> <span className="font-mono">{(100 * utu).toFixed(2)} {r.code}</span>
                      </>
                    );
                  })()}
                </TableCell>
                <TableCell className="text-xs whitespace-nowrap">
                  <div className="text-slate-700 font-mono">{fmtDate(r.updated_at)}</div>
                  <div className="text-slate-400">by {r.updated_by || "—"}</div>
                </TableCell>
                <TableCell className="text-center">
                  <div className="inline-flex items-center gap-2">
                    {r.visible ? <Eye className="w-3.5 h-3.5 text-emerald-600" /> : <EyeOff className="w-3.5 h-3.5 text-rose-500" />}
                    <Switch
                      data-testid={`currency-visible-${r.code}`}
                      checked={!!r.visible}
                      onCheckedChange={() => toggle(r)}
                      disabled={r.is_base || savingCode === r.code}
                    />
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-10">
                {rows.length === 0
                  ? <>No currencies in the rate book yet. Add one on <Link to="/admin/fx" className="text-[var(--brand-primary-dark)] underline">FX Rates</Link>.</>
                  : "No currencies match your filter."}
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      <Card className="p-4 bg-sky-50/40 border-sky-200">
        <div className="text-xs text-slate-600 leading-relaxed">
          <strong className="text-[var(--brand-accent)]">How this works</strong> — The B2C currency picker (`/api/fx/public-rates`) reads this visibility list every time a customer opens the picker. Hidden currencies disappear from the dropdown but never affect the conversion math: a customer who already booked in a now-hidden currency will still see their booking priced correctly because the persisted FX trail is immutable.
        </div>
      </Card>
    </div>
  );
}
